/**
 * scheduleHealth.js
 * Returns a color status based on start date, end date, progress, and today.
 *
 * GREEN  → on track (progress >= time elapsed, or completed)
 * ORANGE → behind schedule (progress lags time elapsed)
 * RED    → past deadline and not completed
 */

export const HEALTH = {
  GREEN:  { label: 'On Track',  color: '#16a34a', bg: '#f0fdf4', border: '#16a34a' },
  ORANGE: { label: 'At Risk',   color: '#d97706', bg: '#fffbeb', border: '#d97706' },
  RED:    { label: 'Overdue',   color: '#dc2626', bg: '#fef2f2', border: '#dc2626' },
  GREY:   { label: 'No Dates / Not Started', color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' },
};

export function getScheduleHealth(startDate, endDate, progress, status) {
  // Completed is always green
  if (status === 'completed') return HEALTH.GREEN;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = startDate ? new Date(startDate) : null;
  const end   = endDate   ? new Date(endDate)   : null;

  // No dates set — neutral grey
  if (!start && !end) return HEALTH.GREY;

  // Past deadline and not done → red
  if (end && today > end) return HEALTH.RED;

  // Not started yet → grey
  if (start && today < start) return HEALTH.GREY;

  // Calculate time elapsed as a percentage of total duration
  if (start && end) {
    const totalDays   = (end - start) / (1000 * 60 * 60 * 24);
    const elapsedDays = (today - start) / (1000 * 60 * 60 * 24);
    const timeElapsedPct = Math.min(100, (elapsedDays / totalDays) * 100);

    // On track if progress is within 15% of time elapsed
    if (progress >= timeElapsedPct - 15) return HEALTH.GREEN;
    return HEALTH.ORANGE;
  }

  // Only end date — if not past it, green
  if (end && today <= end) return HEALTH.GREEN;

  return HEALTH.GREY;
}

// Due within the next 2 days (and not yet completed/overdue) — used to
// trigger a gentle "needs attention soon" pulse, distinct from being
// actually overdue or generally behind on progress.
export function isDueSoon(endDate, status, daysThreshold = 2) {
  if (!endDate || status === 'completed') return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  if (today > end) return false; // already overdue — handled separately
  const daysUntilDue = (end - today) / (1000 * 60 * 60 * 24);
  return daysUntilDue >= 0 && daysUntilDue <= daysThreshold;
}